'use client';

import { useEffect, useRef, useState } from 'react';
import { Download, Eye, FileText, Loader2, Printer, Save } from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import type { ExpedienteDetalle } from '@/lib/expedientes';
import { listarFirmantes, type Firmante } from '@/lib/preRadicacion';
import { guardarDocumentoDecision } from '@/lib/decisionFlujo';
import {
  CAMPOS_AUTO_INHIBITORIO, prefillAutoInhibitorio, blobAutoInhibitorio,
  descargarAutoInhibitorio, decisionEsBorrador, type DatosAutoInhibitorio,
} from '@/lib/plantillas/auto-inhibitorio';
import { superponerBorrador } from '@/lib/plantillas/marca-agua';
import { bannerImpresion } from '@/lib/plantillas/imprimir';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

/**
 * Editor + previsualizador del Auto Inhibitorio (Art. 209).
 * - Editores (abogado/técnico/admin): formulario editable + previsualización + descargas.
 * - Solo lectura (Director/supervisión): solo previsualización + descargar PDF (si aprueba;
 *   si requiere cambios, se devuelve con comentarios al abogado/técnico).
 * La previsualización usa el .docx real (con logos/cabeceras/pie) vía docx-preview.
 */
export default function AutoInhibitorioDialog({
  exp, onClose, soloLectura = false, guardado, onGuardado,
}: {
  exp: ExpedienteDetalle;
  onClose: () => void;
  soloLectura?: boolean;
  guardado?: Record<string, string> | null; // borrador ya persistido
  onGuardado?: () => void;
}) {
  // Arranca del borrador guardado (si existe) sobre los datos autogenerados del expediente.
  const [datos, setDatos] = useState<DatosAutoInhibitorio>(() => ({
    ...prefillAutoInhibitorio(exp),
    ...(guardado ?? {}),
  }));
  const [render, setRender] = useState(false);
  const [expPdf, setExpPdf] = useState(false);
  const [guardando, setGuardando] = useState(false);
  // Firmantes (para estampar la imagen de firma real de cada uno) + la decisión (con
  // los IDs de quién propuso / dio visto bueno / aprobó): las firmas se acumulan según avanza.
  const [firmantes, setFirmantes] = useState<Firmante[] | null>(null);
  const decision = exp.decision_inicial;
  const previewRef = useRef<HTMLDivElement>(null);
  const yaRenderizo = useRef(false);

  useEffect(() => {
    listarFirmantes().then(setFirmantes).catch(() => setFirmantes([]));
  }, []);

  const set = (k: string, v: string) => setDatos((d) => ({ ...d, [k]: v }));

  async function previsualizar() {
    setRender(true);
    try {
      const blob = await blobAutoInhibitorio(datos, decision, firmantes ?? []);
      const { renderAsync } = await import('docx-preview');
      // Leemos el ref DESPUÉS del await: en el doble-montaje de StrictMode el div se
      // recrea, así renderizamos en el contenedor vivo (no en uno ya desmontado).
      const host = previewRef.current;
      if (!host) return;
      host.innerHTML = '';
      await renderAsync(blob, host, undefined, {
        className: 'docx', inWrapper: true, ignoreLastRenderedPageBreak: true,
      });
      // Marca de agua BORRADOR sobre cada página mientras el director no apruebe.
      if (decisionEsBorrador(decision)) superponerBorrador(host);
    } catch (e) {
      avisoError('No se pudo generar la previsualización', e instanceof Error ? e.message : undefined);
    } finally {
      setRender(false);
    }
  }

  // Previsualización automática al abrir (una sola vez). Espera a que carguen los
  // firmantes para estampar las firmas en el primer render (no antes).
  useEffect(() => {
    if (firmantes === null || yaRenderizo.current) return;
    yaRenderizo.current = true;
    void previsualizar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmantes]);

  async function guardarBorrador() {
    setGuardando(true);
    try {
      await guardarDocumentoDecision(exp.id, datos);
      avisoExito('Borrador guardado. El Director revisará este texto.');
      onGuardado?.();
    } catch (e) {
      avisoError('No se pudo guardar el borrador', e instanceof Error ? e.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  async function descargarDocx() {
    try {
      await descargarAutoInhibitorio(datos.RADICADO, datos, decision, firmantes ?? []);
      avisoExito('Auto inhibitorio descargado (.docx).');
    } catch (e) {
      avisoError('No se pudo descargar el documento', e instanceof Error ? e.message : undefined);
    }
  }

  // Exporta a PDF renderizando el .docx real (con logos) en una ventana e invocando
  // la impresión del navegador → "Guardar como PDF". Cero infraestructura y fiel
  // (texto vectorial). Para PDF byte-perfecto server-side, ver Gotenberg (pendiente).
  async function descargarPDF() {
    setExpPdf(true);
    try {
      const blob = await blobAutoInhibitorio(datos, decision, firmantes ?? []);
      const { renderAsync } = await import('docx-preview');
      const w = window.open('', '_blank', 'width=920,height=1000');
      if (!w) {
        avisoError('Permita las ventanas emergentes para exportar a PDF.');
        return;
      }
      w.document.title = `auto-inhibitorio-${datos.RADICADO || 'documento'}`;
      // Evita que el cuerpo tape el logo de la cabecera (ver globals.css).
      const st = w.document.createElement('style');
      st.textContent = '.docx-wrapper section.docx > header, .docx-wrapper section.docx > footer { z-index: 2; }';
      w.document.head.appendChild(st);
      await renderAsync(blob, w.document.body, undefined, { className: 'docx', inWrapper: true });
      if (decisionEsBorrador(decision)) superponerBorrador(w.document.body);
      // La instrucción de «Guardar como PDF» va en la ventana de impresión, no en
      // la app: así no queda un aviso estorbando aquí al volver de imprimir.
      bannerImpresion(w);
      // Da tiempo a que carguen las imágenes (logos) antes de imprimir.
      setTimeout(() => { w.focus(); w.print(); }, 500);
    } catch (e) {
      avisoError('No se pudo exportar a PDF', e instanceof Error ? e.message : undefined);
    } finally {
      setExpPdf(false);
    }
  }

  const panelPreview = (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-muted-foreground">Previsualización</span>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={previsualizar} disabled={render}>
            {render ? <Loader2 className="size-3.5 animate-spin" /> : <Eye className="size-3.5" />}
            Actualizar
          </Button>
          {!soloLectura && (
            <Button size="sm" variant="outline" onClick={guardarBorrador} disabled={guardando}>
              {guardando ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
              Guardar borrador
            </Button>
          )}
          {!soloLectura && (
            <Button size="sm" variant="outline" onClick={descargarDocx}>
              <Download className="size-3.5" /> .docx
            </Button>
          )}
          <Button size="sm" onClick={descargarPDF} disabled={expPdf}>
            {expPdf ? <Loader2 className="size-3.5 animate-spin" /> : <Printer className="size-3.5" />}
            Descargar PDF
          </Button>
        </div>
      </div>
      {/* El contenedor de docx-preview NO debe tener hijos de React. */}
      <div className="relative max-h-[74vh] overflow-auto rounded-md border bg-neutral-100 text-black">
        {render && (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 p-4 text-center text-sm text-gray-500">
            Generando…
          </div>
        )}
        <div ref={previewRef} className="p-2" />
      </div>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[80vw] max-w-[80vw] sm:max-w-[80vw]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="size-5 text-primary" /> Auto inhibitorio (Art. 209)
          </DialogTitle>
          <DialogDescription>
            {soloLectura
              ? 'Previsualización del documento con los datos del expediente. Si requiere cambios, devuélvalo con comentarios; si lo aprueba, descárguelo en PDF.'
              : 'Los campos vienen prellenados con los datos del expediente. Edítelos, previsualice y descargue.'}
          </DialogDescription>
        </DialogHeader>

        {soloLectura ? (
          panelPreview
        ) : (
          <div className="grid gap-6 md:grid-cols-[340px_1fr]">
            {/* Formulario editable (columna angosta fija) */}
            <div className="max-h-[74vh] space-y-3 overflow-y-auto pr-2">
              {CAMPOS_AUTO_INHIBITORIO.map((c) => (
                <div key={c.key} className="space-y-1.5">
                  <Label htmlFor={`f-${c.key}`}>{c.label}</Label>
                  {c.multiline ? (
                    <Textarea id={`f-${c.key}`} rows={4} value={datos[c.key] ?? ''}
                      onChange={(e) => set(c.key, e.target.value)} />
                  ) : (
                    <Input id={`f-${c.key}`} value={datos[c.key] ?? ''}
                      onChange={(e) => set(c.key, e.target.value)} />
                  )}
                </div>
              ))}
            </div>
            {panelPreview}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
